// Keyframe evaluation (port of stereo_render::timeline; tested against the WASM build).

import { CAMERA_TRACK, type Camera, type Easing, type Key, type SceneDesc, type Transform, type Vec3 } from './types';

export function ease(e: Easing | undefined, u: number): number {
  u = Math.min(1, Math.max(0, u));
  switch (e ?? 'linear') {
    case 'ease_in':
      return u * u * u;
    case 'ease_out':
      return 1 - (1 - u) ** 3;
    case 'ease_in_out':
      return u < 0.5 ? 4 * u * u * u : 1 - (-2 * u + 2) ** 3 / 2;
    case 'step':
      return 0;
    default:
      return u;
  }
}

function sample(keys: Key[], t: number, get: (k: Key) => Vec3 | undefined): Vec3 | undefined {
  const defined = keys
    .map((k) => [k, get(k)] as const)
    .filter((x): x is readonly [Key, Vec3] => !!x[1])
    .sort((a, b) => a[0].time - b[0].time);
  if (!defined.length) return undefined;
  const first = defined[0];
  const last = defined[defined.length - 1];
  if (t <= first[0].time) return first[1];
  if (t >= last[0].time) return last[1];
  for (let i = 0; i + 1 < defined.length; i++) {
    const [k0, v0] = defined[i];
    const [k1, v1] = defined[i + 1];
    if (t >= k0.time && t < k1.time) {
      const u = ease(k0.easing, (t - k0.time) / Math.max(1e-6, k1.time - k0.time));
      return [v0[0] + (v1[0] - v0[0]) * u, v0[1] + (v1[1] - v0[1]) * u, v0[2] + (v1[2] - v0[2]) * u];
    }
  }
  return last[1];
}

export interface Pose {
  camera: Camera;
  local: Record<string, Transform>;
}

export function evaluate(scene: SceneDesc, t: number): Pose {
  const tracks = new Map(scene.timeline.tracks.map((tr) => [tr.target, tr.keys]));
  const camera = { ...scene.camera };
  const ck = tracks.get(CAMERA_TRACK);
  if (ck) {
    camera.position = sample(ck, t, (k) => k.position) ?? camera.position;
    camera.target = sample(ck, t, (k) => k.target) ?? camera.target;
  }
  const local: Record<string, Transform> = {};
  for (const n of scene.nodes) {
    const keys = tracks.get(n.id);
    local[n.id] = keys
      ? {
          position: sample(keys, t, (k) => k.position) ?? n.transform.position,
          rotation: sample(keys, t, (k) => k.rotation) ?? n.transform.rotation,
          scale: sample(keys, t, (k) => k.scale) ?? n.transform.scale,
        }
      : n.transform;
  }
  return { camera, local };
}
